import { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { PublicFooter, PublicHeader } from '@/components/AppShell'
import { CatalogCard } from '@/components/CatalogCard'
import { Telemetry } from '@/lib/telemetry'
import { COMMONS_AUTH_ERROR_KEY } from '@/lib/commonsAccess'
import { catalog } from '@/lib/catalog'
import { FAQS } from '@/lib/faqs'
import { LEARN_ARTICLES } from '@/lib/learn'

/**
 * Public landing page for CTDCommons.
 *
 * Two-column layout on >=lg: SEO content (~2/3) left, conversion rail
 * (~1/3) right. The sign-in form has moved to /auth/login (SigninPage).
 *
 * Flash redirect: if AuthContext wrote a deny flash into sessionStorage
 * (commons access denied), redirect to /auth/login so the user sees the
 * error on the sign-in page rather than a silent landing page.
 */
export function LandingPage() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (sessionStorage.getItem(COMMONS_AUTH_ERROR_KEY)) {
      navigate('/auth/login', { replace: true })
    }
  }, [navigate])

  // BrowserRouter doesn't auto-scroll to hash anchors on pushState
  // navigation. When the user arrives at /#archive (e.g. via the Browse
  // nav link), scroll to the element manually.
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1))
      el?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [location.hash])

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-6 py-10 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
            <div className="lg:col-span-2">
              <LandingContent />
            </div>
            <div>
              <RightRail />
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Left column
// ---------------------------------------------------------------------------

function LandingContent() {
  return (
    <article className="space-y-10">
      <header className="space-y-4">
        <p className="text-[11px] text-slate-500 uppercase tracking-wider">
          IND submission archive · powered by Chrona
        </p>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-brand-charcoal leading-tight">
          Real IND submission examples you can read.
        </h1>
        <p className="text-base text-slate-600 leading-relaxed">
          CTDCommons is a free, public archive of complete IND submission
          packages donated by biotech sponsors and contributed by
          open-science researchers. Every dossier is real, every module is
          intact, and every page is searchable — built for regulatory
          professionals who need the actual artifact, not a template.
        </p>

        <Link
          to="/auth/signup"
          onClick={() =>
            Telemetry.createAccountCtaClicked({ placement: 'hero' })
          }
          className="inline-block px-5 py-2.5 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors"
        >
          Create free account →
        </Link>
      </header>

      <CatalogSection />

      <LearnSection />

      <section className="space-y-3" id="audience">
        <h2 className="text-xl font-semibold text-brand-charcoal">
          Who uses CTDCommons
        </h2>
        <p className="text-sm text-slate-600 leading-relaxed">
          Regulatory affairs leads at small and mid-size biotechs preparing
          first-in-human INDs benchmark cover letters, Module 2 summaries,
          and CMC narratives against real cleared submissions instead of
          generic templates.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          Independent regulatory consultants use CTDCommons as a working
          reference library when advising sponsors on submission structure,
          FDA correspondence patterns, and modality-specific filing
          precedent.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          Regulatory affairs fellows and trainees learn the eCTD format from
          real, complete dossiers — the kind of artifact you cannot get from
          a textbook or a course.
        </p>
      </section>

      <section className="space-y-3" id="anatomy">
        <h2 className="text-xl font-semibold text-brand-charcoal">
          What you'll find
        </h2>
        <p className="text-sm text-slate-600 leading-relaxed">
          Every IND in the archive includes the full eCTD module structure:
        </p>
        <ul className="space-y-2 text-sm text-slate-600 leading-relaxed">
          <li>
            <strong className="text-brand-charcoal">Cover letter</strong> —
            the short narrative that frames the submission for the FDA
            review division.
          </li>
          <li>
            <strong className="text-brand-charcoal">
              Module 1 — administrative
            </strong>{' '}
            — Form 1571, Form 3674, environmental analysis, and US-specific
            administrative documents.
          </li>
          <li>
            <strong className="text-brand-charcoal">
              Module 2 — summaries
            </strong>{' '}
            — quality, nonclinical, and clinical summaries written for the
            FDA review team.
          </li>
          <li>
            <strong className="text-brand-charcoal">
              Module 3 — quality / CMC
            </strong>{' '}
            — drug substance, drug product, manufacturing process, and
            analytical control documents.
          </li>
          <li>
            <strong className="text-brand-charcoal">
              Module 4 — nonclinical
            </strong>{' '}
            — pharmacology, pharmacokinetics, and toxicology study reports.
          </li>
          <li>
            <strong className="text-brand-charcoal">
              Module 5 — clinical
            </strong>{' '}
            — clinical study reports, protocols, and investigator brochures.
          </li>
        </ul>
      </section>

      <FAQSection />

      <section className="space-y-3" id="about">
        <h2 className="text-xl font-semibold text-brand-charcoal">
          The partnership
        </h2>
        <p className="text-sm text-slate-600 leading-relaxed">
          CTDCommons is operated by{' '}
          <a
            href="https://chronabio.ai"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              Telemetry.outboundClicked({
                destination: 'chronabio_pro',
                placement: 'landing_body',
              })
            }
            className="text-brand-orange hover:underline"
          >
            Chrona
          </a>{' '}
          in partnership with 1Day Sooner and Renaissance Philanthropy.
          Chrona's commercial product is a regulatory document intelligence
          platform that helps biotechs prepare IND submissions. 1Day Sooner
          and Renaissance Philanthropy support the underlying mission. Learn
          about the CTDCommons mission and fund at{' '}
          <a
            href="https://ctdcommons.org"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              Telemetry.outboundClicked({
                destination: 'ctdcommons_org',
                placement: 'landing_body',
              })
            }
            className="text-brand-orange hover:underline"
          >
            ctdcommons.org
          </a>
          .
        </p>
      </section>
    </article>
  )
}

function FAQSection() {
  return (
    <section className="space-y-2" id="faq">
      <h2 className="text-xl font-semibold text-brand-charcoal">
        Frequently asked questions
      </h2>
      <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
        {FAQS.map((faq) => (
          <details key={faq.question} className="group py-4">
            <summary className="flex items-start justify-between gap-4 cursor-pointer list-none text-base font-medium text-brand-charcoal hover:text-brand-orange transition-colors">
              <span>{faq.question}</span>
              <span
                aria-hidden
                className="mt-1 shrink-0 text-xs text-slate-400 transition-transform group-open:rotate-180"
              >
                ▼
              </span>
            </summary>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              {faq.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  )
}

function LearnSection() {
  return (
    <section className="space-y-4" id="learn">
      <h2 className="text-xl font-semibold text-brand-charcoal">
        Learn from the archive
      </h2>
      <p className="text-sm text-slate-600 leading-relaxed">
        Field guides drawn from the dossiers themselves — what the FDA's
        language really means, and what a decade of regulatory dialogue
        actually looks like.
      </p>
      <ul className="space-y-3">
        {LEARN_ARTICLES.map((a) => (
          <li key={a.slug}>
            <Link
              to={`/learn/${a.slug}`}
              className="group block rounded-lg border border-slate-200 px-4 py-3 hover:border-brand-orange/60 hover:shadow-sm transition-all"
            >
              <span className="text-sm font-medium text-brand-charcoal group-hover:text-brand-orange transition-colors">
                {a.title}
              </span>
              <span className="block text-xs text-slate-500 mt-0.5">
                {a.dek}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

function CatalogSection() {
  return (
    <section className="space-y-4" id="archive">
      <h2 className="text-xl font-semibold text-brand-charcoal">
        What's in the archive
      </h2>
      <ul className="space-y-4">
        {catalog.inds.map((ind) => (
          <li key={ind.slug}>
            <CatalogCard
              ind={ind}
              linkTo={`/ind/${ind.slug}`}
              onLinkClick={() =>
                Telemetry.catalogIndClicked({ ind_slug: ind.slug })
              }
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Right rail — conversion blocks
// ---------------------------------------------------------------------------

function RightRail() {
  return (
    <div className="lg:sticky lg:top-6 space-y-4">
      <StatsBlock />
      <FeaturedDossierCard />
      <PartnershipBlock />
    </div>
  )
}

/**
 * Both totals pin 'en-US' for the same reason CatalogCard's metadata line
 * does: the SEO prerender (inject-catalog-plugin.ts) formats these numbers
 * in Node with the build host's locale, and React re-formats them with the
 * visitor's. Any drift shows up as a visible flicker on mount.
 */
function StatsBlock() {
  const portfolioCount = catalog.inds.length
  const submissionTotal = catalog.inds.reduce(
    (sum, ind) => sum + (ind.submission_count ?? 0),
    0,
  )
  const documentTotal = catalog.inds.reduce(
    (sum, ind) => sum + (ind.document_count ?? 0),
    0,
  )

  return (
    <div className="bg-slate-50 rounded-lg px-5 py-5">
      <p className="text-xs text-slate-500 uppercase tracking-widest mb-4">
        What's inside
      </p>
      <div className="space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold text-brand-charcoal leading-none">
            {portfolioCount}
          </span>
          <span className="text-xs text-slate-500">IND portfolios</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold text-brand-charcoal leading-none">
            {submissionTotal.toLocaleString('en-US')}
          </span>
          <span className="text-xs text-slate-500">submission sequences</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold text-brand-charcoal leading-none">
            {documentTotal.toLocaleString('en-US')}
          </span>
          <span className="text-xs text-slate-500">documents</span>
        </div>
      </div>
      <p className="mt-4 text-xs text-slate-400">
        Pre-IND correspondence included
      </p>
    </div>
  )
}

function FeaturedDossierCard() {
  const featured = catalog.inds.find((ind) => ind.name === 'ALTU-237')
  const submissionCount = featured?.submission_count ?? 0

  return (
    <div className="bg-white rounded-lg border border-slate-300 px-5 py-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className="text-base font-semibold text-brand-charcoal">
          ALTU-237
        </span>
        <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-md bg-brand-orange/10 text-[10px] font-medium text-brand-orange">
          Featured
        </span>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">
        {submissionCount} submission sequences spanning years of FDA
        correspondence — the full longitudinal record of a regulatory
        conversation.
      </p>
      <Link
        to="/auth/signup"
        onClick={() =>
          Telemetry.createAccountCtaClicked({ placement: 'featured_card' })
        }
        className="mt-4 inline-block text-sm text-brand-orange hover:underline"
      >
        Browse this portfolio →
      </Link>
    </div>
  )
}

function PartnershipBlock() {
  return (
    <div className="px-5 py-4">
      <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-3">
        In partnership with
      </p>
      <img
        src="/partnership-lockup.png"
        alt="1Day Sooner and Renaissance Philanthropy"
        className="h-7 w-auto"
        draggable={false}
      />
    </div>
  )
}
